import Image from "next/image";
import { formatIndianNumber } from "@/utils/commonFunctions";
import Icon from "@/components/icons/Icons";
import { motion } from "framer-motion";
import { ROOT_URL } from "@/utils/gloableVariables";

type FundItem = {
  id?: number;
  scheme_name?: string;
  scheme_code?: string;
  amc_code?: string;
  amc?: string;
  url?: string;
  category_type?: string;
  asset_id?: number;
  sip_allowed?: number;
  lump_allowed?: number;
  minimum_sip_invest?: number;
  minimum_investment?: number;
  six_month_abs?: number;
  one_year_abs?: number;
  three_year?: number;
};

export default function FundCardComponent({
  fund,
  isSelected,
  investType,
  onSelect,
}: {
  fund: FundItem;
  isSelected: boolean;
  investType: "sip" | "lumpsum";
  onSelect: () => void;
}) {
  const minSIP = formatIndianNumber(fund.minimum_sip_invest ?? "-");
  const minLump = formatIndianNumber(fund.minimum_investment ?? "-");

  return (
    <div
      onClick={onSelect}
      className={`relative w-67.5 xs:w-72 md:w-80 rounded-2xl p-3.5 border-2 bg-[#FFFFFF26] transition duration-300 overflow-hidden cursor-pointer ${isSelected ? "border-white" : "border-transparent  hover:border-white/30 hover:scale-[1.01]"}`}
    >
      <div className="text-2sm font-semibold h-10 flex items-center gap-2">
        <Image
          src={`${ROOT_URL}img/AMCsLogo/${fund.amc_code}.png`}
          alt={fund?.scheme_name || "Fund Icon"}
          width={28}
          height={28}
          className="w-7 h-7 overflow-hidden shrink-0 border border-[#E9E9EB] rounded-full"
        />

        <p className="line-clamp-2 leading-5">{fund.scheme_name}</p>
      </div>

      <div className="grid grid-cols-3 gap-2 mt-4 text-sm">
        <div>
          <p className="text-[#C6C6C6] text-xs">
            Min {investType === "sip" ? "SIP" : "Lump."}
          </p>
          <p className={`font-semibold text-2sm leading-4 mt-1 text-white/95`}>
            ₹{investType === "sip" ? minSIP : minLump}
          </p>
        </div>

        <div>
          <p className="text-[#C6C6C6] text-xs">Returns (1Y)</p>
          <p
            className={`font-semibold text-2sm leading-4 mt-1 ${fund?.one_year_abs && fund?.one_year_abs > 0 ? "text-[#00F12C]" : "text-[#FF0000]"}`}
          >
            {fund.one_year_abs ?? "-"}%
          </p>
        </div>

        <div>
          <p className="text-[#C6C6C6] text-xs">Returns (3Y)</p>
          <p
            className={`font-semibold text-2sm leading-4 mt-1 ${fund?.three_year && fund?.three_year > 0 ? "text-[#00F12C]" : "text-[#FF0000]"}`}
          >
            {fund.three_year ?? "-"}%
          </p>
        </div>
      </div>

      {isSelected && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeOut" }}
            className="absolute inset-0 bg-[#00000026] pointer-events-none"
          >
            <span className="absolute w-5 h-5 rounded-full bg-white top-1.5 right-1.5 flex items-center justify-center shadow-[0_0_80px_60px_#ffffff33]">
              <Icon.rightSignIcon className="text-black w-3 h-3" />
            </span>
          </motion.div>
        </>
      )}
    </div>
  );
}
